<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-changed="$_queryHandler_hasQueryChanged"
            :query-count="$_queryHandler_numberOfQueries"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details outlined />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.language"
                    :items="mappedLanguages"
                    label="Language"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.tag"
                    :value="query.params.tag"
                    :items="mappedTags"
                    label="Tags"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <v-data-table
                :headers="tableHeaders"
                :items="filteredTranslations"
                :footer-props="tableFooterProps"
                :loading="isLoading"
                fixed-width
                disable-sort
              >
                <template v-slot:item="{ item }">
                  <v-hover v-slot="{ hover }">
                    <tr>
                      <td>
                        <v-tooltip max-width="300" bottom>
                          <template #activator="{ on }">
                            <div class="text-truncate" style="max-width: 350px;" v-on="on">
                              {{ item.name }}
                            </div>
                          </template>
                          <span>{{ item.name }}</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <v-tooltip max-width="300" bottom>
                          <template #activator="{ on }">
                            <div class="text-truncate" style="max-width: 350px;" v-on="on">
                              {{ item.text[query.params.language] }}
                            </div>
                          </template>
                          <span>{{ item.text[query.params.language] }}</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <template v-if="item.tags.length">
                          <v-tooltip max-width="200" bottom>
                            <template #activator="{ on }">
                              <base-chip color="primary" :text="String(item.tags.length)" v-on="on" />
                            </template>
                            <span>{{ item.tags.join(', ') }}</span>
                          </v-tooltip>
                        </template>
                        <base-chip v-else text="0" />
                      </td>
                      <td align="right">
                        <div v-show="hover">
                          <more-menu>
                            <v-list-item
                              :disabled="!isPermittedTo('update')"
                              :inactive="!isPermittedTo('update')"
                              @click="onClickEdit(item)"
                            >
                              <v-list-item-title>
                                Update Translation
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                              :disabled="!isPermittedTo('delete')"
                              :inactive="!isPermittedTo('delete')"
                              @click="onClickDelete(item)"
                            >
                              <v-list-item-title>Delete Translation</v-list-item-title>
                            </v-list-item>
                          </more-menu>
                        </div>
                        <div v-if="!hover" style="min-width:57px"></div>
                      </td>
                    </tr>
                  </v-hover>
                </template>
              </v-data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <generic-modal
      v-if="showImportModal"
      v-model="showImportModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      confirm-text="Upload csv"
      title="Import translations"
      @modal-confirm="onImportConfirm"
      @modal-cancel="onImportCancel"
    >
      <div class="mb-2">
        Please make sure to upload your translations as a
        <code>.csv</code>
        file. The format of the CSV should match the one that you downloaded via the export tool.
      </div>
      <drag-and-drop-upload v-model="importFile" accept=".csv, application/vnd.ms-excel" />
    </generic-modal>

    <generic-modal
      v-if="showCreateModal"
      v-model="showCreateModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      confirm-text="Add translation"
      title="Create new translation"
      @modal-confirm="onCreateConfirm"
      @modal-cancel="onCreateCancel"
    >
      <v-row>
        <v-col>
          <text-input v-model="currentTranslation.name" class="mb-4" label="Name" hide-details outlined />
          <v-combobox
            v-model="currentTranslation.tags"
            :items="tags"
            class="mb-4"
            label="Tags"
            deletable-chips
            dense
            hint="You can add your own tags, too!"
            multiple
            outlined
            persistent-hint
            small-chips
          />
          <v-translation-wrapper :languages="languages">
            <template v-slot:default="{ language }">
              <v-text-field
                v-model="currentTranslation.text[language.code]"
                :label="`Translation (${formattedOption('languages', language.code)})`"
              />
            </template>
          </v-translation-wrapper>
        </v-col>
      </v-row>
    </generic-modal>

    <generic-modal
      v-if="showUpdateModal"
      v-model="showUpdateModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      confirm-text="Update translation"
      title="Update translation"
      @modal-confirm="onUpdateConfirm"
      @modal-cancel="onUpdateCancel"
    >
      <v-row>
        <v-col>
          <text-input v-model="currentTranslation.slug" class="mb-4" label="Slug" disabled hide-details outlined />
          <text-input v-model="currentTranslation.name" class="mb-4" label="Name" hide-details outlined />
          <v-combobox
            v-model="currentTranslation.tags"
            :items="tags"
            class="mb-4"
            label="Tags"
            deletable-chips
            dense
            hint="You can add your own tags, too!"
            multiple
            outlined
            persistent-hint
            small-chips
          />
          <v-translation-wrapper :languages="languages">
            <template v-slot:default="{ language }">
              <v-text-field
                v-model="currentTranslation.text[language.code]"
                :label="`Translation (${formattedOption('languages', language.code)})`"
              />
            </template>
          </v-translation-wrapper>
        </v-col>
      </v-row>
    </generic-modal>

    <delete-modal
      v-if="showDeleteModal"
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="Translation"
      item="this translation"
      @modal-confirm="onDeleteConfirm"
      @modal-cancel="onDeleteCancel"
    />

    <fab-actions
      context="Translations"
      @create="showCreateModal = true"
      @import="showImportModal = true"
      @export="exportTranslations"
    />
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import { languages, translations } from '@/services/offers'

import BaseChip from '@/components/chips/base-chip'
import DeleteModal from '@/components/modals/delete-modal'
import DragAndDropUpload from '@/components/drag-and-drop-upload'
import FabActions from '@/components/fab-actions'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import MoreMenu from '@/components/more-menu'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'
import VTranslationWrapper from '@/components/translation-wrapper'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { exportCSV } from '@/utils/csv'

const translationModel = () => ({
  name: null,
  tags: [],
  text: {},
})

export default {
  name: 'TranslationsIndex',
  components: {
    BaseChip,
    DeleteModal,
    DragAndDropUpload,
    FabActions,
    FilterAccordion,
    GenericModal,
    MoreMenu,
    PageLoader,
    SearchInput,
    SelectList,
    TextInput,
    VTranslationWrapper,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    languages: [],
    translations: [],
    currentTranslation: translationModel(),
    tableHeaders: [
      { text: 'Name', value: 'name', width: '40%' },
      { text: 'Text', value: 'text', width: '40%' },
      { text: 'Tags', value: 'tags' },
      { text: '', align: 'end', width: 77 },
    ],
    tableFooterProps: {
      itemsPerPageOptions: [20, 50, 100, -1],
    },
    queryDefaults: ['language'],
    queriesDefined: ['language', 'searchTerm', 'tag'],
    importFile: null,
    showImportModal: false,
    showCreateModal: false,
    showUpdateModal: false,
    showDeleteModal: false,
  }),
  computed: {
    ...mapGetters('options', ['formattedOption']),
    mappedLanguages() {
      return this.languages.map(({ name, code }) => ({ key: name, value: code }))
    },
    tags() {
      return this.translations.reduce((acc, { tags }) => {
        const reduced = tags.filter(tag => !acc.includes(tag))
        return [...acc, ...reduced]
      }, [])
    },
    mappedTags() {
      if (!this.tags) return []
      return this.tags.map(tag => ({ key: tag, value: tag }))
    },
    filteredTranslations() {
      const {
        params: { language, tag },
        searchTerm,
      } = this.query
      return this.translations.filter(
        ({ tags, name, text }) =>
          (!tag || tags.includes(tag)) &&
          (!searchTerm ||
            name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            text[language]?.toLowerCase()?.includes(searchTerm.toLowerCase()))
      )
    },
  },
  async created() {
    await Promise.all([this.getLanguages(), this.getTranslations()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations({ setPageLoading: 'general/setPageLoading' }),
    async getLanguages() {
      try {
        this.languages = await languages.getLanguages()
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to fetch languages' })
      }
    },
    async createTranslation(translation) {
      this.isLoading = true
      try {
        await translations.createTranslation(translation)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to create translation',
        })
      }
      this.isLoading = false
    },
    async getTranslations() {
      this.isLoading = true
      try {
        this.translations = await translations.getFiltered()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translations',
        })
      }
      this.isLoading = false
    },
    async updateTranslation(translation) {
      this.isLoading = true
      try {
        await translations.updateTranslation(translation.id, translation)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to update translation',
        })
      }
      this.isLoading = false
    },
    async deleteTranslation(translation) {
      this.isLoading = true

      try {
        await translations.deleteTranslation(translation.id)
        this.$notify({
          type: 'success',
          text: 'Successfully removed translation',
        })
        await this.getTranslations()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete translation',
        })
      }

      this.isLoading = false
    },
    async importTranslations() {
      this.isLoading = true
      try {
        await translations.importTranslations(this.importFile)
        this.$notify({ type: 'success', text: 'Imported translations' })
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to import translations' })
      }
      this.isLoading = false
    },
    async exportTranslations() {
      this.loading = true
      try {
        const data = await translations.exportTranslations()
        exportCSV(data, 'translation-export.csv')
      } catch (err) {
        this.$notify({ type: 'error', text: 'Unable to export translations' })
      }
      this.loading = false
    },
    onClickEdit(translation) {
      this.showUpdateModal = true
      this.currentTranslation = translation
    },
    onClickDelete(translation) {
      this.showDeleteModal = true
      this.currentTranslation = translation
    },
    async onCreateConfirm() {
      await this.createTranslation(this.currentTranslation)
      await this.getTranslations()
      this.showCreateModal = false
      this.currentTranslation = translationModel()
    },
    onCreateCancel() {
      this.showCreateModal = false
      this.currentTranslation = translationModel()
    },
    async onUpdateConfirm() {
      await this.updateTranslation(this.currentTranslation)
      await this.getTranslations()
      this.showUpdateModal = false
      this.currentTranslation = translationModel()
    },
    onUpdateCancel() {
      this.currentTranslation = translationModel()
    },
    async onDeleteConfirm() {
      await this.deleteTranslation(this.currentTranslation)
      await this.getTranslations()
      this.showDeleteModal = false
      this.currentTranslation = translationModel()
    },
    onDeleteCancel() {
      this.currentTranslation = translationModel()
    },
    async onImportConfirm() {
      await this.importTranslations()
      this.showImportModal = false
      this.importFile = null
      await this.getTranslations()
    },
    onImportCancel() {
      this.showImportModal = false
      this.importFile = null
    },
  },
}
</script>
